import React, { useState, useEffect } from 'react';
import { Calendar, User, Building2, Filter, Eye, Loader2, MessageSquare, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Conversation {
  id: string;
  protocolId: string;
  customerPhone: string;
  customerName: string | null;
  status: string;
  origin?: string;
  departmentId: string | null;
  assignedAgentId: string | null;
  startedAt: string;
  closedAt: string | null;
  department: {
    id: string;
    name: string;
  } | null;
  assignedAgent: {
    id: string;
    name: string;
    email: string;
  } | null;
  messages?: Array<{
    id: string;
    timestamp: string;
  }>;
}

interface Department {
  id: string;
  name: string;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  role: string;
}

const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 13 && cleaned.startsWith('55')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  } else if (cleaned.length === 11) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

const getStatusBadge = (status: string) => {
  const statusConfig: Record<string, { label: string; colorClass: string }> = {
    WAITING: { label: 'Aguardando', colorClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    MENU: { label: 'Menu', colorClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    QUEUED: { label: 'Na Fila', colorClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
    ACTIVE: { label: 'Ativo', colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    CLOSED: { label: 'Fechado', colorClass: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
    AVALIACAO: { label: 'Avaliação', colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    ANSWERED: { label: 'Respondido', colorClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' },
  };

  const config = statusConfig[status] || { label: status, colorClass: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${config.colorClass}`}>
      {config.label}
    </span>
  );
};

const getOriginBadge = (origin?: string) => {
  if (origin === 'BULK_DISPATCH') {
    return (
      <span className="px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 rounded text-xs font-medium">
        Disparo
      </span>
    );
  } else if (origin === 'AGENT') {
    return (
      <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded text-xs font-medium">
        Atendente
      </span>
    );
  }
  return (
    <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded text-xs font-medium">
      Cliente
    </span>
  );
};

export const ConversationsTablePanel: React.FC = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters - Default: today
  const [dateFilterType, setDateFilterType] = useState<'start' | 'lastMessage'>('start'); // 'start' or 'lastMessage'
  const [startDate, setStartDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [lastMessageStartDate, setLastMessageStartDate] = useState('');
  const [lastMessageEndDate, setLastMessageEndDate] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState('');
  const [orderBy, setOrderBy] = useState('lastMessageAt');

  const itemsPerPage = 10;

  useEffect(() => {
    loadDepartments();
    loadAgents();
  }, []);

  useEffect(() => {
    if (!isCollapsed) {
      console.log('[ConversationsTablePanel] Panel expanded, loading conversations...');
      loadConversations();
    } else {
      // Clear data when collapsed
      console.log('[ConversationsTablePanel] Panel collapsed, clearing data');
      setConversations([]);
      setTotal(0);
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (!isCollapsed) {
      console.log('[ConversationsTablePanel] Page changed to:', currentPage);
      loadConversations();
    }
  }, [currentPage]);

  const loadDepartments = async () => {
    try {
      const data = await api.getDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadAgents = async () => {
    try {
      const data = await api.getUsers({});
      setAgents(data.filter(agent => agent.role !== 'ADMIN'));
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = {
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        orderBy: orderBy,
      };

      // Date range filter - only send based on selected filter type
      if (dateFilterType === 'start') {
        // Conversation start date filter
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      } else if (dateFilterType === 'lastMessage') {
        // Last message date filter
        if (lastMessageStartDate) params.lastMessageStartDate = lastMessageStartDate;
        if (lastMessageEndDate) params.lastMessageEndDate = lastMessageEndDate;
      }

      // Optional filters
      if (selectedDepartmentId) params.departmentId = selectedDepartmentId;
      if (selectedStatus) params.status = selectedStatus;
      if (selectedAgentId) params.assignedAgentId = selectedAgentId;
      if (selectedOrigin) params.origin = selectedOrigin;

      // Build query string
      const queryString = new URLSearchParams(params).toString();
      const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/conversations?${queryString}&_t=${Date.now()}`;

      console.log('[ConversationsTablePanel] Loading conversations with params:', params);
      console.log('[ConversationsTablePanel] API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'X-Skip-Cache': 'true',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversations');
      }

      const data = await response.json();
      console.log('[ConversationsTablePanel] API Response:', data);
      console.log('[ConversationsTablePanel] Conversations count:', data.conversations?.length);
      console.log('[ConversationsTablePanel] Total from API:', data.pagination?.total);

      setConversations(data.conversations || []);
      setTotal(data.pagination?.total || 0);
    } catch (error: any) {
      console.error('[ConversationsTablePanel] Failed to load conversations:', error);
      setError(error.message || 'Falha ao carregar atendimentos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterApply = () => {
    console.log('[ConversationsTablePanel] Applying filters:', {
      startDate,
      endDate,
      lastMessageStartDate,
      lastMessageEndDate,
      selectedDepartmentId,
      selectedStatus,
      selectedAgentId,
      selectedOrigin,
      orderBy
    });
    setCurrentPage(1);
    loadConversations();
  };

  const handleDateFilterTypeChange = (type: 'start' | 'lastMessage') => {
    setDateFilterType(type);
    // Note: We don't clear the other filter's dates - just disable them
    // This way if user switches back, their previous selection is preserved
  };

  const handleClearFilters = () => {
    setDateFilterType('start');
    setStartDate(format(new Date(), 'yyyy-MM-dd'));
    setEndDate(format(new Date(), 'yyyy-MM-dd'));
    setLastMessageStartDate('');
    setLastMessageEndDate('');
    setSelectedDepartmentId('');
    setSelectedStatus('');
    setSelectedAgentId('');
    setSelectedOrigin('');
    setOrderBy('lastMessageAt');
    setCurrentPage(1);
    // Reload after clearing filters
    setTimeout(() => {
      if (!isCollapsed) {
        loadConversations();
      }
    }, 100);
  };

  const handleViewConversation = (conversationId: string) => {
    setLoadingConversationId(conversationId);
    setTimeout(() => {
      navigate(`/conversations/${conversationId}?status=all`);
    }, 150);
  };

  const totalPages = Math.ceil((total || 0) / itemsPerPage) || 1;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border-t-4 border-cyan-500">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
              <MessageSquare className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Listagem de Atendimentos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Atendimentos de hoje - Use filtros e ordenação para personalizar
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isCollapsed && (
              <button
                onClick={loadConversations}
                disabled={isLoading}
                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition disabled:opacity-50"
                title="Atualizar"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition"
              title={isCollapsed ? 'Expandir' : 'Colapsar'}
            >
              {isCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {!isCollapsed && (
      <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros</h4>
        </div>

        {/* Date Filter Type Selection */}
        <div className="flex items-center gap-4 mb-3 pb-2 border-b border-gray-200 dark:border-gray-600">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Filtro de Data:</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              name="dateFilterType"
              value="start"
              checked={dateFilterType === 'start'}
              onChange={() => handleDateFilterTypeChange('start')}
              className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-700 dark:text-gray-300">Início da Conversa</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              name="dateFilterType"
              value="lastMessage"
              checked={dateFilterType === 'lastMessage'}
              onChange={() => handleDateFilterTypeChange('lastMessage')}
              className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-700 dark:text-gray-300">Última Mensagem</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {/* Start Date - Início Conversa */}
          <div>
            <label className={`block text-xs font-medium mb-0.5 ${dateFilterType === 'start' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
              Início Conversa (De)
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={dateFilterType !== 'start'}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {/* End Date - Início Conversa */}
          <div>
            <label className={`block text-xs font-medium mb-0.5 ${dateFilterType === 'start' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
              Início Conversa (Até)
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={dateFilterType !== 'start'}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {/* Last Message Start Date */}
          <div>
            <label className={`block text-xs font-medium mb-0.5 ${dateFilterType === 'lastMessage' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
              Última Msg (De)
            </label>
            <input
              type="date"
              value={lastMessageStartDate}
              onChange={(e) => setLastMessageStartDate(e.target.value)}
              disabled={dateFilterType !== 'lastMessage'}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {/* Last Message End Date */}
          <div>
            <label className={`block text-xs font-medium mb-0.5 ${dateFilterType === 'lastMessage' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
              Última Msg (Até)
            </label>
            <input
              type="date"
              value={lastMessageEndDate}
              onChange={(e) => setLastMessageEndDate(e.target.value)}
              disabled={dateFilterType !== 'lastMessage'}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {/* Department */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
              Departamento
            </label>
            <select
              value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todos</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todos</option>
              <option value="WAITING">Aguardando</option>
              <option value="MENU">Menu</option>
              <option value="QUEUED">Na Fila</option>
              <option value="ACTIVE">Ativo</option>
              <option value="CLOSED">Fechado</option>
              <option value="AVALIACAO">Avaliação</option>
              <option value="ANSWERED">Respondido</option>
            </select>
          </div>

          {/* Agent */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
              Atendente
            </label>
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todos</option>
              <option value="unassigned">Não atribuído</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
          </div>

          {/* Origin */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
              Origem
            </label>
            <select
              value={selectedOrigin}
              onChange={(e) => setSelectedOrigin(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todas</option>
              <option value="CLIENT">Cliente</option>
              <option value="BULK_DISPATCH">Disparo</option>
              <option value="AGENT">Atendente</option>
            </select>
          </div>

          {/* Order By */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
              Ordenar Por
            </label>
            <select
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="lastMessageAt">Última Mensagem</option>
              <option value="startedAt">Primeira Mensagem</option>
            </select>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex gap-2 mt-2.5">
          <button
            onClick={handleFilterApply}
            disabled={isLoading}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm rounded-lg transition disabled:opacity-50"
          >
            Aplicar Filtros
          </button>
          <button
            onClick={handleClearFilters}
            className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 text-sm rounded-lg transition"
          >
            Limpar Filtros
          </button>
        </div>
      </div>
      )}

      {/* Content */}
      {!isCollapsed && (
      <div className="px-6 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {error}
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>Nenhum atendimento encontrado com os filtros selecionados</p>
          </div>
        ) : (
          <>
            <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              Total: {total || 0} atendimento{(total || 0) !== 1 ? 's' : ''} | Página {currentPage} de {totalPages}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Protocolo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Departamento
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Atendente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Origem
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Primeira Mensagem
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Última Mensagem
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ação
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {conversations.map((conversation) => (
                    <tr key={conversation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      {/* Protocolo */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-mono font-semibold">
                          #{conversation.protocolId}
                        </span>
                      </td>

                      {/* Cliente */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {conversation.customerName || 'Sem nome'}
                        </span>
                      </td>

                      {/* Telefone */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                          {formatPhoneNumber(conversation.customerPhone)}
                        </span>
                      </td>

                      {/* Departamento */}
                      <td className="px-4 py-3">
                        {conversation.department ? (
                          <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                            <Building2 className="w-4 h-4" />
                            <span>{conversation.department.name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-xs">Não atribuído</span>
                        )}
                      </td>

                      {/* Atendente */}
                      <td className="px-4 py-3">
                        {conversation.assignedAgent ? (
                          <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                            <User className="w-4 h-4" />
                            <span>{conversation.assignedAgent.name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-xs">Não atribuído</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(conversation.status)}
                      </td>

                      {/* Origem */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getOriginBadge(conversation.origin)}
                      </td>

                      {/* Primeira Mensagem */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                          <Calendar className="w-3 h-3" />
                          {format(parseISO(conversation.startedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </div>
                      </td>

                      {/* Última Mensagem */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                          <Calendar className="w-3 h-3" />
                          {conversation.messages && conversation.messages.length > 0
                            ? format(parseISO(conversation.messages[0].timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                            : format(parseISO(conversation.startedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </div>
                      </td>

                      {/* Ação - Ver Conversa */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewConversation(conversation.id)}
                          disabled={loadingConversationId === conversation.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-xs rounded transition disabled:opacity-70 disabled:cursor-not-allowed"
                          title="Ver conversa"
                        >
                          {loadingConversationId === conversation.id ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Abrindo...
                            </>
                          ) : (
                            <>
                              <Eye className="w-3.5 h-3.5" />
                              Ver
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  ← Anterior
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 rounded text-sm transition ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  );
};
